import { useKeyPressEvent } from 'react-use'
import styled, { css } from 'styled-components'

import { ArrowLeft2Icon, Button, Typography } from '@blade/ui-kit'
import { useBreakpoint } from '@root/shared/hooks'

import type { IconProps } from '@blade/ui-kit'
import type { Intent } from '@root/shared/ui-kit'

export type AuthModalHeadingIntent = Extract<
  Intent,
  'neutral' | 'success' | 'error'
>

export type AuthModalHeadingProps = {
  title?: string
  intent?: AuthModalHeadingIntent
  description?: string
  mobileInfoMessage?: string
  onBack?: () => void
  variant?: 'default' | 'secondary'
  icon?: React.FC<IconProps>
}

export const AuthModalHeading: React.FC<AuthModalHeadingProps> = ({
  title,
  intent = 'neutral',
  description,
  onBack,
  icon,
  variant = 'default',
  mobileInfoMessage,
}) => {
  const isMobile = useBreakpoint() === 'mobile'
  useKeyPressEvent('Escape', onBack)

  if (variant === 'default') {
    const Icon = icon

    return (
      <Root $withIcon>
        <IconSeparator>
          {onBack ? (
            <SeparatorStartGroup>
              <SeparatorStartChunk $intent={intent} />
              <BackButtonSecondary
                variant="secondary"
                size="xxs"
                onClick={onBack}
                iconLeft={<ArrowLeft2Icon />}
              />
              <SeparatorEndChunk $intent={intent} />
            </SeparatorStartGroup>
          ) : (
            <SeparatorStart $intent={intent} />
          )}
          {Icon ? (
            <Outline $intent={intent}>
              <OutlineInner>
                <Icon size={24} />
              </OutlineInner>
            </Outline>
          ) : (
            <Title
              $variant={variant}
              css={css`
                padding-inline: 6px;
              `}
            >
              {title}
            </Title>
          )}
          <SeparatorEnd $intent={intent} />
        </IconSeparator>
        {Icon ? (
          <HeadingWrapper>
            {isMobile ? (
              <MobileInfoMessage>{mobileInfoMessage}</MobileInfoMessage>
            ) : undefined}
            <Heading>
              <Title $variant={variant}>{title}</Title>
              {description ? (
                <Description $variant={variant}>{description}</Description>
              ) : undefined}
            </Heading>
          </HeadingWrapper>
        ) : undefined}
      </Root>
    )
  }

  return (
    <Root>
      {onBack ? (
        <Button
          variant="ghost"
          size="xxs"
          onClick={onBack}
          iconLeft={<ArrowLeft2Icon />}
        />
      ) : (
        <div />
      )}
      <Heading>
        <Title $variant={variant}>{title}</Title>
        {description ? (
          <Description $variant={variant}>{description}</Description>
        ) : undefined}
      </Heading>
    </Root>
  )
}

const Root = styled.div<{ $withIcon?: boolean }>`
  position: relative;
  display: grid;
  align-content: start;
  /* align-content: center; */
  /* align-items: center; */

  ${({ $withIcon }) =>
    $withIcon
      ? css`
          gap: 18px;
        `
      : css`
          grid-template-columns: 1fr auto 1fr;
        `}
`

const Title = styled.h2<{
  $variant: 'default' | 'secondary'
}>`
  color: var(--text-12);
  text-align: center;
  font-style: normal;

  ${({ $variant }) => {
    switch ($variant) {
      case 'default': {
        return css`
          font-size: 15px;
          font-weight: 600;
        `
      }
      case 'secondary': {
        return css`
          font-size: 18px;
          font-weight: 700;
        `
      }
    }
  }}
`

const Description = styled.p<{ $variant: 'default' | 'secondary' }>`
  color: var(--textA-9);
  text-align: center;

  ${({ $variant }) => {
    switch ($variant) {
      case 'default': {
        return css`
          font-size: 12px;
          font-weight: 400;
        `
      }
      case 'secondary': {
        return css`
          font-size: 14px;
          font-weight: 400;
        `
      }
    }
  }}
`

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`

const IconSeparator = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  align-content: center;
`

const SeparatorStartGroup = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-content: center;
  align-items: center;
`

const SeparatorStartChunk = styled.span<{ $intent?: Intent }>`
  height: 1px;
  width: 15px;

  ${({ $intent }) => {
    switch ($intent) {
      case 'neutral': {
        return css`
          background: linear-gradient(
            270deg,
            var(--backgroundA-6) 10%,
            transparent 100%
          );
        `
      }
      case 'success': {
        return css`
          background: linear-gradient(
            270deg,
            rgba(var(--primary-9-rgb), 0.5) 10%,
            transparent 100%
          );
        `
      }
      case 'error': {
        return css`
          background: linear-gradient(
            270deg,
            rgba(var(--error-9-rgb), 0.5) 10%,
            transparent 100%
          );
        `
      }
    }
  }}
`

const SeparatorEndChunk = styled.span<{ $intent?: Intent }>`
  height: 1px;

  ${({ $intent }) => {
    switch ($intent) {
      case 'neutral': {
        return css`
          background: linear-gradient(
            270deg,
            var(--backgroundA-6) 60%,
            var(--backgroundA-6) 100%
          );
        `
      }
      case 'success': {
        return css`
          background: linear-gradient(
            270deg,
            var(--primary-9) 60%,
            rgba(var(--primary-9-rgb), 0.8) 100%
          );
        `
      }
      case 'error': {
        return css`
          background: linear-gradient(
            270deg,
            var(--error-9) 60%,
            rgba(var(--error-9-rgb), 0.8) 100%
          );
        `
      }
    }
  }}
`
const SeparatorStart = styled.span<{ $intent?: AuthModalHeadingIntent }>`
  height: 1px;
  transition: background 0.15s linear;

  ${({ $intent }) => {
    switch ($intent) {
      case 'neutral': {
        return css`
          background: linear-gradient(
            270deg,
            var(--backgroundA-6) 70%,
            transparent 100%
          );
        `
      }
      case 'success': {
        return css`
          background: linear-gradient(
            270deg,
            rgba(var(--primary-9-rgb), 1) 60%,
            transparent 100%
          );
        `
      }
      case 'error': {
        return css`
          background: linear-gradient(
            270deg,
            rgba(var(--error-9-rgb), 1) 60%,
            transparent 100%
          );
        `
      }
    }
  }}
`

const SeparatorEnd = styled.span<{ $intent?: AuthModalHeadingIntent }>`
  height: 1px;
  transition: background-color 0.15s linear;

  ${({ $intent }) => {
    switch ($intent) {
      case 'neutral': {
        return css`
          background: linear-gradient(
            90deg,
            var(--backgroundA-6) 70%,
            transparent 100%
          );
        `
      }
      case 'success': {
        return css`
          background: linear-gradient(
            90deg,
            var(--primary-9) 60%,
            transparent 100%
          );
        `
      }
      case 'error': {
        return css`
          background: linear-gradient(
            90deg,
            var(--error-9) 60%,
            transparent 100%
          );
        `
      }
    }
  }}
`

const Outline = styled.div<{ $intent?: AuthModalHeadingIntent }>`
  border-radius: 100%;
  display: grid;
  place-content: center;
  padding: 4px;

  transition: border-color 0.15s linear;
  border: 1px solid var(--backgroundA-6);

  ${({ $intent }) => {
    switch ($intent) {
      case 'neutral': {
        return css`
          border-color: var(--backgroundA-6);
        `
      }
      case 'success': {
        return css`
          border-color: var(--primaryA-9);
        `
      }
      case 'error': {
        return css`
          border-color: var(--errorA-9);
        `
      }
    }
  }}
`

const OutlineInner = styled.div`
  background: var(--appearanceA-8);

  border-radius: 100%;
  display: grid;

  width: 34px;
  height: 34px;
  place-content: center;
`

const BackButtonSecondary = styled(Button)<{
  $intent?: AuthModalHeadingIntent
}>`
  transition: border 0.15s linear;

  ${({ $intent }) => {
    switch ($intent) {
      case 'neutral': {
        return css`
          border: 1px solid var(--backgroundA-6);
        `
      }
      case 'success': {
        return css`
          border: 1px solid var(--primary-9);
        `
      }
      case 'error': {
        return css`
          border: 1px solid rgba(var(--error-9-rgb), 1);
        `
      }
    }
  }}
`

const MobileInfoMessage = styled(Typography).attrs({
  variant: 'mobile-10-regular',
})`
  text-align: center;
  color: var(--textA-10);
`
